/**
 * @file Normalização e validação de destinos de serviço do EP.
 * @module tools/serviceTarget
 *
 * `normalizeServiceTarget()` é a referência canônica para as propriedades do
 * objeto de destino usado por Ajax, Combo, AutoForm e demais consumidores.
 * Outros arquivos devem apontar para a função com `@see`, sem criar nomes de
 * tipos paralelos.
 */

const VALID_SCOPES = new Set(['product', 'shared']);

/**
 * Normaliza texto opcional para string não vazia ou `null`.
 *
 * @param {*} value Valor recebido.
 * @returns {string|null} Texto normalizado.
 */
const normalizeText = (value) => {
  const normalized = String(value ?? '').trim();
  return normalized || null;
};

/**
 * Normaliza um destino de serviço para o contrato vigente.
 *
 * Regras principais:
 * - `scope` default é `product`;
 * - `service` usa `service` e aceita `category` como fallback legado;
 * - `category` é preenchido com o próprio `service` quando ausente;
 * - `product` é removido de targets `shared`.
 *
 * Esta função apenas normaliza. Use `assertServiceTarget()` quando precisar
 * também validar obrigatoriedade e escopo.
 *
 * @param {Object} [input={}] Destino bruto.
 * @param {'product'|'shared'|string} [input.scope='product'] Escopo técnico.
 * @param {string} [input.product] Produto; obrigatório somente no escopo `product`.
 * @param {string} [input.service] Serviço técnico canônico.
 * @param {string} [input.category] Alias legado de `service`.
 * @param {string} [input.domain] Domínio técnico.
 * @param {string} [input.module] Módulo técnico.
 * @param {string} [input.method] Operação/método chamado.
 * @returns {{scope:string,product:(string|null),service:(string|null),category:(string|null),domain:(string|null),module:(string|null),method:(string|null)}} Destino normalizado.
 * @see assertServiceTarget
 *
 * @example
 * normalizeServiceTarget({
 *   scope: 'product',
 *   product: 'platform',
 *   service: 'accounts',
 *   domain: 'tenancy',
 *   module: 'tenants',
 *   method: 'getConfigScreen'
 * });
 *
 * @example
 * normalizeServiceTarget({
 *   scope: 'shared',
 *   service: 'reference',
 *   domain: 'geography',
 *   module: 'catalog',
 *   method: 'getDataCombo'
 * });
 */
export function normalizeServiceTarget(input = {}) {
  const scope = normalizeText(input?.scope)?.toLowerCase() || 'product';
  const service = normalizeText(input?.service ?? input?.category);
  const category = normalizeText(input?.category) || service;
  const inputProduct = normalizeText(input?.product)?.toLowerCase() || null;

  return {
    scope,
    // Shared é ownership técnico fora de produto. Um target genérico pode trazer
    // `product`, mas esse valor não participa da chamada quando scope = shared.
    product: scope === 'shared' ? null : inputProduct,
    service,
    category,
    domain: normalizeText(input?.domain),
    module: normalizeText(input?.module),
    method: normalizeText(input?.method),
  };
}

/**
 * Valida um destino e retorna a mensagem de erro sem lançar exceção.
 *
 * São obrigatórios `service`, `domain`, `module` e `method`. `product` também é
 * obrigatório quando o escopo é `product`.
 *
 * @param {Object} [input={}] Destino bruto ou normalizado.
 * @returns {string|null} Mensagem de erro ou `null` quando válido.
 * @see normalizeServiceTarget
 */
export function getServiceTargetError(input = {}) {
  const target = normalizeServiceTarget(input);

  if (!VALID_SCOPES.has(target.scope)) {
    return `scope de serviço inválido: ${target.scope || '(vazio)'}`;
  }

  const missing = ['service', 'domain', 'module', 'method']
    .filter((key) => !target[key]);

  if (target.scope === 'product' && !target.product) {
    missing.unshift('product');
  }

  if (missing.length > 0) {
    return `Campos obrigatórios ausentes na chamada de serviço: ${missing.join(', ')}`;
  }

  return null;
}

/**
 * Normaliza e valida um destino de serviço.
 *
 * @param {Object} [input={}] Destino bruto; veja {@link normalizeServiceTarget}.
 * @returns {Object} Destino normalizado e válido.
 * @throws {Error} Quando o escopo é inválido ou faltam campos obrigatórios.
 * @see normalizeServiceTarget
 * @see getServiceTargetError
 */
export function assertServiceTarget(input = {}) {
  const target = normalizeServiceTarget(input);
  const error = getServiceTargetError(target);
  if (error) throw new Error(error);
  return target;
}

/**
 * Constrói o path HTTP de uma operação a partir do destino validado.
 *
 * O `product` e o `scope` não fazem parte do path final. A rota produzida é
 * `/<service>/<domain>/<module>/<method>`, com cada segmento codificado.
 *
 * @param {Object} [input={}] Destino da chamada; veja {@link normalizeServiceTarget}.
 * @returns {string} Path relativo codificado.
 * @throws {Error} Propaga erros de `assertServiceTarget()`.
 * @see normalizeServiceTarget
 * @see assertServiceTarget
 */
export function buildServicePath(input = {}) {
  const target = assertServiceTarget(input);
  return `/${[
    target.service,
    target.domain,
    target.module,
    target.method,
  ].map((part) => encodeURIComponent(part)).join('/')}`;
}
